import { useLocalization } from "@/context/localization-context";
import type { TranslationKey } from "@/i18n/messages";
import { cn } from "@/lib/utils";
import { errorMessage, useMonitoringDependencies } from "../../api";
import { Ago, EmptyBlock, ErrorBlock, FetchingDot, LoadingBlock, SectionCard, StatePill } from "../../components/primitives";
import { formatMs } from "../../format";
import type { DependencyCheck } from "../../types";
import { SubHeading } from "./stat-tile";
import { LIMITS, toneText } from "./tone";

const KNOWN = ["mongo", "s3", "thingsboard"];

/** Reachability of MongoDB, S3, ThingsBoard and the GPU pods, as checked by the backend. */
export function DependenciesCard() {
  const { t } = useLocalization();
  const query = useMonitoringDependencies();
  const checks = query.data?.checks ?? [];
  const services = checks.filter((check) => check.kind !== "pod");
  const pods = checks.filter((check) => check.kind === "pod");

  return (
    <SectionCard
      title={t("monitoring.backend.deps.title")}
      description={t("monitoring.backend.deps.description")}
      actions={
        <>
          <FetchingDot active={query.isFetching} />
          {query.data?.generated_at && (
            <span className="text-xs text-muted-foreground">
              {t("monitoring.backend.deps.checked")} <Ago ts={query.data.generated_at} />
            </span>
          )}
        </>
      }
    >
      {query.isPending ? (
        <LoadingBlock height={140} />
      ) : query.isError && !query.data ? (
        <ErrorBlock message={errorMessage(query.error)} onRetry={() => void query.refetch()} />
      ) : checks.length === 0 ? (
        <EmptyBlock>{t("monitoring.backend.deps.empty")}</EmptyBlock>
      ) : (
        <div className="flex flex-col gap-4">
          {query.isError && <ErrorBlock message={errorMessage(query.error)} onRetry={() => void query.refetch()} />}
          <div className="flex flex-col divide-y divide-border/60">
            {services.map((check) => (
              <CheckRow key={check.name} check={check} />
            ))}
          </div>
          <div className="flex flex-col gap-1">
            <SubHeading>{t("monitoring.backend.deps.pods")}</SubHeading>
            {pods.length === 0 ? (
              <p className="py-1 text-xs text-muted-foreground">{t("monitoring.backend.deps.podsEmpty")}</p>
            ) : (
              <div className="flex flex-col divide-y divide-border/60">
                {pods.map((check) => (
                  <CheckRow key={check.name} check={check} />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </SectionCard>
  );
}

function CheckRow({ check }: { check: DependencyCheck }) {
  const { t } = useLocalization();
  const ok = check.status === "ok";
  const isPod = check.kind === "pod";
  const name = isPod ? check.name.replace(/^pod\s+/, "") : check.name;
  const slow = ok && !isPod && (check.ms ?? 0) > LIMITS.dependencySlowMs;
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2">
      <div className="min-w-0 flex-1">
        {isPod ? (
          <span className="break-all font-mono text-xs">{name}</span>
        ) : (
          <>
            <span className="text-sm">
              {KNOWN.includes(name) ? t(`monitoring.backend.deps.${name}` as TranslationKey) : name}
            </span>
            <span className="ml-2 font-mono text-[11px] text-muted-foreground">{name}</span>
          </>
        )}
      </div>
      <span
        className={cn("font-mono text-xs tabular-nums", slow ? toneText("warn") : "text-muted-foreground")}
        title={isPod ? t("monitoring.backend.deps.pingHint") : undefined}
      >
        {formatMs(check.ms)}
        {slow && ` · ${t("monitoring.backend.deps.slow")}`}
      </span>
      <StatePill tone={ok ? (slow ? "warn" : "ok") : "nok"}>
        {ok ? t("monitoring.backend.deps.ok") : t("monitoring.backend.deps.error")}
      </StatePill>
      {check.error && (
        <p className={cn("basis-full break-words font-mono text-[11px] leading-snug", ok ? "text-muted-foreground" : "text-red-300")}>
          {check.error}
        </p>
      )}
    </div>
  );
}
